import React, { Component } from 'react';
import styled from 'styled-components';
import {
  CellMeasurer,
  CellMeasurerCache,
  InfiniteLoader,
  List
} from 'react-virtualized';
import { connect } from 'react-redux';
import { appsPerPage } from 'config';
import IOSSpinner from 'inline-icons/iOS-loading.svg';

const DEFAULT_APP_HEIGHT = 80;
const DEFAULT_CLOCK_HEIGHT = 160;

const StyledVirtualizedList = styled(List)`
  background-color: white;
`;

const LoadingRow = styled.div.attrs({
  dangerouslySetInnerHTML: {
    __html: IOSSpinner
  }
})`
  display: flex;
  padding: 2px;
  align-items: center;
  justify-content: center;
  background: white;
  min-height: 100px;
  svg {
    ${({ theme }) => theme.LoadingIcon}
  }
`;

interface VirtualizedListProps {
  location?: any;
  currentCount: number;
  fetchMore: any;
  maxCount?: number;
  pageLoadThreshold: number;
  pageSize: number;
  isItemLoaded: ({}: any) => boolean;
  isApp: boolean;
  navbarHeight: number;
  listHeader?: React.ReactNode;
}

class VirtualizedList extends Component<VirtualizedListProps> {
  private listRef?: any;
  private readonly _cache: any;

  constructor(props: VirtualizedListProps) {
    super(props);

    const { isApp } = props;
    this._cache = new CellMeasurerCache({
      // the options need to be here because scrolling goes wild without them
      defaultHeight: isApp ? DEFAULT_APP_HEIGHT : DEFAULT_CLOCK_HEIGHT,
      fixedWidth: true
    });
  }

  _storeKey = 'list-store';

  state = {
    scrollTop: 0,
    loading: false,
    loadingScroll: true
  };

  componentDidMount() {
    if (
      this.props.location.kind === 'pop' &&
      this.props.location.prev.type === 'APP_V2' &&
      this.props.location.type === 'COLLECTION_V2'
    ) {
      this.loadScrollPosition();
      return;
    }

    this.clearScrollPosition();
  }

  componentWillUnmount() {
    if (this.props.location.type === 'COLLECTION_V2') {
      this.saveScrollPosition();
    }
  }

  clearScrollPosition() {
    sessionStorage.removeItem(this._storeKey);
    this.setState({
      loadingScroll: false
    });
  }

  loadScrollPosition() {
    const storeJSON = sessionStorage.getItem(this._storeKey);
    const {
      cellHeightCache = {},
      rowHeightCache = {},
      scrollTop = 0
    } = storeJSON ? JSON.parse(storeJSON) : {};
    (this._cache as any)._rowHeightCache = rowHeightCache;
    (this._cache as any)._cellHeightCache = cellHeightCache;
    this.setState({
      scrollTop,
      loadingScroll: false
    });
  }

  saveScrollPosition() {
    sessionStorage.setItem(
      this._storeKey,
      JSON.stringify({
        cellHeightCache: (this._cache as any)._cellHeightCache,
        rowHeightCache: (this._cache as any)._rowHeightCache,
        scrollTop: this.state.scrollTop
      })
    );
  }

  renderRow = ({
    key, // Unique key within array of rows
    index, // Index of row within collection
    parent,
    style // Style object to be applied to row (to position it)
  }: any) => {
    const { currentCount, children, listHeader } = this.props;

    if (!children) {
      throw new Error('children render prop is required!');
    }

    let itemContent;

    // This ensures an additional row to show loading UX
    // to let the user know the next page is loading
    if (index === currentCount && this.state.loading) {
      itemContent = <LoadingRow />;
    } else if (index === 0 && listHeader) {
      itemContent = (
        <div>
          {listHeader}
          {(children as any)({ index })}
        </div>
      );
    } else {
      itemContent = (children as any)({ index });
    }

    return (
      <CellMeasurer
        cache={this._cache}
        columnIndex={0}
        key={key}
        parent={parent}
        rowIndex={index}
      >
        <div style={style}>
          {itemContent}
        </div>
      </CellMeasurer>
    );
  };

  onScroll = ({ scrollTop }: any) => {
    this.setState({ scrollTop });
  };

  onLoadMoreRows = async (loadMoreArgs: any) => {
    const loadingRow = this.props.currentCount;

    this.setState({ loading: true });
    await this.props.fetchMore(loadMoreArgs);
    this.setState({ loading: false }, () =>
      // Guarantee that it will resize the row to the
      // normal content once loading is complete
      (this._cache as any).clear(loadingRow)
    );
  };

  componentDidUpdate() {
    // Force an update of the list items.
    if (this.listRef) {
      this.listRef.forceUpdateGrid();
    }
  }

  bindListRef = (ref: any) => {
    // Save the list reference to be used in componentDidUpdate().
    this.listRef = ref;
  };

  render() {
    const {
      maxCount = 10000,
      pageLoadThreshold = 6,
      pageSize = appsPerPage,
      currentCount = 0,
      isItemLoaded,
      navbarHeight,
    } = this.props;

    const { loading, scrollTop } = this.state;

    return (
      <InfiniteLoader
        isRowLoaded={isItemLoaded}
        loadMoreRows={this.onLoadMoreRows}
        rowCount={maxCount}
        threshold={pageLoadThreshold}
        minimumBatchSize={pageSize}
      >
        {({ onRowsRendered, registerChild }: any) => (
          <StyledVirtualizedList
            width={window.innerWidth}
            height={window.innerHeight - navbarHeight}
            onScroll={this.onScroll}
            rowCount={currentCount + (loading ? 1 : 0)}
            rowHeight={this._cache.rowHeight}
            rowRenderer={this.renderRow}
            scrollTop={scrollTop}
            onRowsRendered={onRowsRendered}
            ref={(ref: any)=> {
              registerChild(ref);
              this.bindListRef(ref);
            }}
          />
        )}
      </InfiniteLoader>
    );
  }
}

const mapStateToProps = ({ location }: any) => ({
  location,
  fullNavbar: location.type === 'GALLERY'
});

export default connect(mapStateToProps)(VirtualizedList);
